import React from 'react'
import { createRoot } from 'react-dom/client'
import {
  ThemeProvider as StyledThemeProvider,
  createGlobalStyle,
} from 'styled-components'
import type { ExtendedTheme } from '@devnw/theme'
import { ThemeBuilder, SpyderThemeProvider } from '@devnw/theme'
import { IconProvider } from '@devnw/icons'
import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import App from './App'
import { createLocalIconAdapter } from './icons/localIconAdapter'
import { toSpyderTheme } from './theme/spyderTheme'

// ---------------------------------------------------------------------------
// OIDCPAM Gate-6 Phase 7 — MUI/emotion fully removed.
//
// The app now renders under a single styled-components ThemeProvider +
// IconProvider stack. Phase 0 introduced this stack ADDITIVELY, alongside the
// MUI ThemeProvider + CssBaseline that every pre-migration page relied on;
// now that every page/shell has been migrated off MUI (Phases 1-6), the MUI
// provider stack — and the Material UI / Emotion dependencies themselves —
// are removed. See .superpowers/sdd/oidcpam-gate6-shared.md.
// ---------------------------------------------------------------------------

// ThemeBuilder.fromPalette derives a full ExtendedTheme from a minimal
// {primary, accent, danger} palette. `primary` matches the FORMER MUI
// theme's palette.primary.main exactly; no MUI secondary/error was ever
// customised (the old theme.ts relied on MUI's built-in defaults), so
// `accent` and `danger` are brand-appropriate choices rather than a literal
// MUI mirror.
const spyderTheme = ThemeBuilder.fromPalette({
  palette: { primary: '#1a56db', accent: '#10b981', danger: '#991b1b' },
  mode: 'light',
  radius: 8,
})

// ThemeBuilder's minimal-palette input cannot express the app's page
// background or font family (bg.* is fixed per-mode; font.family defaults to
// system-ui) — see gate6-oidcui-inventory.md §1.5. Overlay the FORMER MUI
// values (background.default:#f8fafc, Roboto) so the look stays consistent
// with the pre-migration app. This is a documented gap, not a bug: a
// pixel-perfect ThemeBuilder equivalent of MUI's theme is not required, a
// coherent one is.
const appTheme: ExtendedTheme = {
  ...spyderTheme,
  bg: { ...spyderTheme.bg, canvas: '#f8fafc' },
  font: {
    ...spyderTheme.font,
    family: '"Roboto","Helvetica","Arial",sans-serif',
  },
}

// @devnw/layout's AppShell (see AdminLayout) reads its tokens from
// useSpyderTheme(), which THROWS outside a SpyderThemeProvider. Derive its
// SpyderTheme from the same appTheme so the shell and its contents cannot
// drift apart — see src/theme/spyderTheme.ts.
const spyderShellTheme = toSpyderTheme(appTheme)

// @devnw/theme augments styled-components' DefaultTheme with ExtendedTheme
// (see the package's styled.d.ts triple-slash reference), so `p.theme` below
// is already typed without an explicit generic.
//
// html/body/#root height + #root's flex column: AdminLayout (see
// src/layout/AdminLayout.tsx) now renders @devnw/layout's AppShell, whose
// root View is `flex: 1` — that only has any effect if its ancestor chain up
// to the mount point is both a definite-height chain AND a flex container,
// otherwise the shell can render with no usable height (a blank page) rather
// than filling the viewport. Fixing this here — rather than reintroducing
// `position: fixed`/`margin-left` in AdminLayout — keeps the height concern
// where it belongs (the mount point), not smeared back into the shell.
const GlobalStyle = createGlobalStyle`
  html, body, #root {
    height: 100%;
  }

  #root {
    display: flex;
    flex-direction: column;
  }

  body {
    background-color: ${(p) => p.theme.bg.canvas};
    font-family: ${(p) => p.theme.font.family};
  }
`

// The icon adapter imports a closed set of Font Awesome Free definitions as
// build-time SVG data. No custom-element loader or third-party icon CDN is
// involved, so the authentication UI remains compatible with its self-only
// script/connect CSP even when the browser is offline.
const localIconAdapter = createLocalIconAdapter()

const container = document.getElementById('root')
if (!container) throw new Error('Root element not found')

const root = createRoot(container)
root.render(
  <React.StrictMode>
    <StyledThemeProvider theme={appTheme}>
      {/* @devnw/layout's AppShell (see AdminLayout) reads its tokens from
          useSpyderTheme(), which THROWS outside this provider. Both providers
          carry the same palette via toSpyderTheme so the shell and its
          contents cannot drift apart. */}
      <SpyderThemeProvider theme={spyderShellTheme}>
        <GlobalStyle />
        <IconProvider adapter={localIconAdapter}>
          <App />
        </IconProvider>
      </SpyderThemeProvider>
    </StyledThemeProvider>
  </React.StrictMode>
)
